import React, { useEffect } from 'react';
import { useImmer } from 'use-immer';
import { SelectBoxComponent } from '@/components';
import { cn } from '@/utils';
import FoldedCardIconPortrait from '@/Icons/binding/icon_folded_card_portrait.svg';
import FoldedCardIconLandscape from '@/Icons/binding/icon_folded_card_landscape.svg';
import AccordionModal from "./AccordionModal";

const BookBinding = ({
  isExpanded,
  headline,
  values,
  selection_label,
  toggleDropdown,
  closeDropdown,
  onItemClick,
  parentId,
  loadingIndex,
  previewIconsPath,
  isFetching,
}) => {
  const PREVIEW_ICONS_PREFIX = '/book_binding/';
  const PREVIEW_ICONS_SUFFIX = '_preview';
  const PREVIEW_ICONS_EXTENSION = '.png';
  const ID = 'book-binding';

  const svgIcons = [
    'bin_folded_top',
    'bin_folded_left'
  ];

  const [componentState, updateComponentState] = useImmer({
    isModalOpen: false,
    isSvgIcon: values.find((value) => svgIcons.includes(value.id)),
    activeAccordionIndex: values.findIndex((value) => value.is_selected),
  });

  const openModal = () => {
    updateComponentState((draft) => {
      draft.isModalOpen = true;
    });
  };

  const closeModal = () => {
    updateComponentState((draft) => {
      draft.isModalOpen = false;
      draft.activeAccordionIndex = values.findIndex(
        (value) => value.is_selected,
      );
    });
  };

  const handleItemClick = ({ parentId, id, is_selected }, i) => {
    onItemClick({ parentId, id, is_selected });
    updateComponentState((draft) => {
      draft.activeAccordionIndex = i;
    });
  };

  useEffect(() => {
    document.body.style.overflow = componentState.isModalOpen ? 'hidden' : '';
  }, [componentState.isModalOpen]);

  useEffect(() => {
    if (!loadingIndex && componentState.isModalOpen) {
      closeModal();
    }
  }, [loadingIndex]);

  const selectTooltip = (i) => {
    updateComponentState((draft) => {
      draft.activeAccordionIndex = i;
    });
  };

  const getPreviewImagePath = (id) => {
    // For some reason, we use SVG-Icons for book binding.
    if (svgIcons.includes(id)) {
      return (id === 'bin_folded_top' ? <FoldedCardIconPortrait role='img' className='w-6xl h-[2.75rem] mr-lg' />
        : <FoldedCardIconLandscape role='img' className='w-6xl h-2xl mr-lg' />);
    }

    // We use PNG-Icons for other stuff like "premium finishes"
    return (
      previewIconsPath +
      PREVIEW_ICONS_PREFIX +
      id +
      PREVIEW_ICONS_SUFFIX +
      PREVIEW_ICONS_EXTENSION
    );
  };

  return (
    <>
      <SelectBoxComponent
        id={ID}
        showIcon={values.length > 1}
        onClick={() => toggleDropdown()}
        onClickOutside={() => closeDropdown()}
        onLinkClick={() => openModal()}
        linkText={values.every(value => value.tooltip && value.tooltip.trim() !== '') ? Drupal.t('More information') : false}
        isDisabled={isFetching}
        disabledClassName="opacity-50 bg-gray-100 cursor-wait"
        {...{ isExpanded, headline }}
        DropdownContent={
          <div
            className="absolute z-overlay mt-3xs w-full bg-white shadow border border-neutral-darker rounded overflow-hidden"
          >
            <ul
              role="listbox"
              aria-labelledby={`listbox-${ID}-label`}
              className="dropdown-content overflow-auto m-0 max-h-[50vh] laptop:max-h-[40vh]"
            >
              {values.map(({ label, sub_label, is_selected , id }, i) => (
                <li
                  key={i}
                  id={`listbox-${ID}-${i}`}
                  role="option"
                  tabindex="0"
                  aria-selected={is_selected}
                  data-book-binding={id}
                  className={cn(
                    'relative flex flex-row items-center h-[4.375rem] py-3xs px-lg border-b border-b-neutral-medium cursor-pointer',
                    'hover:bg-neutral-light focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-1',
                    is_selected || loadingIndex === `${parentId}_${id}`
                      ? 'bg-neutral-light ring-2 ring-inset ring-brand-1 border-b-0'
                      : 'bg-white',
                    loadingIndex === `${parentId}_${id}` ? 'opacity-80' : ''
                  )}
                  onClick={() =>
                    handleItemClick({ parentId, id, is_selected }, i)
                  }
                >
                  <div className={cn("flex-shrink-0", !componentState.isSvgIcon ? "w-6xl h-2xl mr-lg" : "")}>
                    <Choose>
                      <When condition={componentState.isSvgIcon}>
                        {getPreviewImagePath(id)}
                      </When>
                      <Otherwise>
                        <img
                          alt=""
                          className="w-full"
                          src={getPreviewImagePath(id)}
                          width="60"
                          height="30"
                        />
                      </Otherwise>
                    </Choose>
                  </div>
                  <div className="flex flex-col leading-5 font-semibold">
                    {label}
                    <div className="font-medium text-xs">{sub_label}</div>
                  </div>
                  <If
                    condition={
                      is_selected || loadingIndex === `${parentId}_${id}`
                    }
                  >
                    <div className="absolute inset-y-0 right-0 pr-lg flex items-center">
                      <Choose>
                        <When condition={loadingIndex === `${parentId}_${id}`}>
                          <ft-spinner size="small"></ft-spinner>
                        </When>
                      </Choose>
                    </div>
                  </If>
                </li>
              ))}
            </ul>
          </div>
        }
      >
        <div className="flex items-center">
          <div className={cn("flex-shrink-0", !componentState.isSvgIcon ? "w-6xl h-2xl mr-lg" : "")}>
            <Choose>
              <When condition={componentState.isSvgIcon}>
                {getPreviewImagePath(values.find((value) => value.is_selected).id)}
              </When>
              <Otherwise>
                <img
                  alt=""
                  className="w-full"
                  src={getPreviewImagePath(values.find((value) => value.is_selected).id)}
                  width="60"
                  height="30"
                />
              </Otherwise>
            </Choose>

          </div>
          <div className="flex flex-col items-start text-left leading-5">
            {selection_label}
          </div>
        </div>
      </SelectBoxComponent>

      <If condition={componentState.isModalOpen}>
        <AccordionModal
          headline={Drupal.t('More information')}
          ctaLabel={Drupal.t('Select')}
          closeModal={() => closeModal()}
          values={values}
          getPreviewImagePath={getPreviewImagePath}
          activeAccordionIndex={componentState.activeAccordionIndex}
          selectTooltip={selectTooltip}
          parentId={parentId}
          onItemClick={onItemClick}
          loadingIndex={loadingIndex}
        />
      </If>
    </>
  );
};

export default BookBinding;
